export interface Choice {
  text: string;
  outcome: string;
  privacyScore: number; // -2 to +2
  xpReward: number;
  next: string | null; // scene id, or null = end
}

export interface Scene {
  id: string;
  chapter: number;
  title: string;
  story: string;
  situation: string;
  choices: Choice[];
}

export const CHAPTERS: { id: number; title: string; tag: string; scenes: string[] }[] = [
  { id: 1, title: 'The Cookie Crumble', tag: 'Data & Cookies', scenes: ['ch1_s1', 'ch1_s2', 'ch1_s3'] },
  { id: 2, title: 'The Algorithm Trap', tag: 'Social Media', scenes: ['ch2_s1', 'ch2_s2', 'ch2_s3'] },
  { id: 3, title: 'The Permission Game', tag: 'App Permissions', scenes: ['ch3_s1', 'ch3_s2', 'ch3_s3'] },
];

export const SCENES: Record<string, Scene> = {
  // ─── Chapter 1: The Cookie Crumble ───────────────────────────────────────
  ch1_s1: {
    id: 'ch1_s1', chapter: 1,
    title: 'A New Website',
    story: 'You open a new tab and visit a cool gaming news site. Before you can read anything, a banner slides up covering half the screen.',
    situation: 'The banner reads: "We use cookies to personalise your experience, show you targeted ads, and share your data with 47 partners. Accept All or Manage Preferences?"',
    choices: [
      {
        text: 'Accept All — I just want to read the article',
        outcome: 'You\'ve just given 47 companies permission to track everywhere you go online. They know your age, location, and what you looked at — and they\'ll remember forever.',
        privacyScore: -2, xpReward: 20,
        next: 'ch1_s2',
      },
      {
        text: 'Manage Preferences — I\'ll set it up myself',
        outcome: 'Smart move! You turn off advertising cookies and third-party sharing. Only essential cookies stay on. The site still works fine.',
        privacyScore: 2, xpReward: 50,
        next: 'ch1_s2',
      },
      {
        text: 'Close the tab — not worth it',
        outcome: 'Maximum privacy. You found another site that doesn\'t demand your data. Not always possible, but a valid choice.',
        privacyScore: 2, xpReward: 30,
        next: 'ch1_s2',
      },
    ],
  },
  ch1_s2: {
    id: 'ch1_s2', chapter: 1,
    title: 'The Invisible Tracker',
    story: 'A week later, you notice ads following you around the internet. You see the same shoe ad on three different websites — shoes you only looked at once.',
    situation: 'Your friend says "just ignore it." Your other friend says you can stop it. What do you do?',
    choices: [
      {
        text: 'Ignore it — ads don\'t bother me',
        outcome: 'The tracking continues. By next month, advertisers know your daily schedule, mood patterns, and what you want before you search for it.',
        privacyScore: -1, xpReward: 10,
        next: 'ch1_s3',
      },
      {
        text: 'Install a browser extension like uBlock Origin',
        outcome: 'You block 94% of trackers. The ads stop following you. Sites actually load faster too — trackers were slowing everything down.',
        privacyScore: 2, xpReward: 60,
        next: 'ch1_s3',
      },
      {
        text: 'Use a private/incognito window from now on',
        outcome: 'Incognito hides your history from others on your device, but doesn\'t stop websites from tracking you. Partial improvement only.',
        privacyScore: 0, xpReward: 30,
        next: 'ch1_s3',
      },
    ],
  },
  ch1_s3: {
    id: 'ch1_s3', chapter: 1,
    title: 'The Data Request',
    story: 'You learn that under Canadian privacy law (PIPEDA), you have the right to ask any company what data they have on you and request it be deleted.',
    situation: 'You remember that shoe website you visited. They probably have a profile on you. Do you exercise your rights?',
    choices: [
      {
        text: 'Send them a data access request',
        outcome: 'They respond with 47 pages of data — every click, scroll, time spent on each product, your inferred income bracket, and age estimate. Eye-opening.',
        privacyScore: 2, xpReward: 70,
        next: null,
      },
      {
        text: 'Request deletion of all my data',
        outcome: 'Under PIPEDA, they must delete your data within 30 days. Your profile disappears. They have to comply by law.',
        privacyScore: 2, xpReward: 80,
        next: null,
      },
      {
        text: 'It\'s too much effort',
        outcome: 'Fair — the process is intentionally complicated. This is a privacy by design problem. Companies should make data deletion as easy as sign-up.',
        privacyScore: -1, xpReward: 20,
        next: null,
      },
    ],
  },

  // ─── Chapter 2: The Algorithm Trap ───────────────────────────────────────
  ch2_s1: {
    id: 'ch2_s1', chapter: 2,
    title: 'The Infinite Scroll',
    story: 'You open a social app to check one notification. Forty minutes later you\'re still there, watching videos about topics you didn\'t even know you cared about.',
    situation: 'The app\'s algorithm has learned exactly what keeps you watching. It shows content that triggers curiosity, mild anxiety, and excitement — the perfect mix to keep you scrolling.',
    choices: [
      {
        text: 'Keep scrolling — this content is actually good',
        outcome: 'The algorithm notes your engagement. It now knows that emotional content keeps you hooked and will escalate the intensity to maintain your attention.',
        privacyScore: -1, xpReward: 10,
        next: 'ch2_s2',
      },
      {
        text: 'Set a screen time limit on this app',
        outcome: 'You limit the app to 20 minutes. The algorithm can\'t collect as much data about you, and you\'ve taken back control of your attention.',
        privacyScore: 1, xpReward: 50,
        next: 'ch2_s2',
      },
      {
        text: 'Go to settings and turn off personalized recommendations',
        outcome: 'The feed becomes chronological. Less addictive, but you actually see what you chose to follow — not what the algorithm decided you should see.',
        privacyScore: 2, xpReward: 70,
        next: 'ch2_s2',
      },
    ],
  },
  ch2_s2: {
    id: 'ch2_s2', chapter: 2,
    title: 'The Filter Bubble',
    story: 'Your friend sends you a news article. You\'ve never seen anything like it on your feed — even though it\'s major news. Your algorithm has been hiding it from you.',
    situation: 'You only see content the algorithm predicts you\'ll agree with. You\'re in a filter bubble — a personalised reality that feels complete but is actually very narrow.',
    choices: [
      {
        text: 'Search for opposing viewpoints deliberately',
        outcome: 'You find perspectives you\'d never encountered. It\'s uncomfortable but valuable. The algorithm starts adjusting your bubble slightly.',
        privacyScore: 1, xpReward: 60,
        next: 'ch2_s3',
      },
      {
        text: 'Follow accounts I disagree with to break the bubble',
        outcome: 'Bold move. Your feed diversifies immediately. The algorithm is confused at first, then adapts. You get a more balanced view of the world.',
        privacyScore: 2, xpReward: 70,
        next: 'ch2_s3',
      },
      {
        text: 'My feed shows real news — this must be fake',
        outcome: 'This is exactly what filter bubbles do: they make your personalised view feel like the full truth. This is one of the most dangerous effects of algorithmic curation.',
        privacyScore: -2, xpReward: 10,
        next: 'ch2_s3',
      },
    ],
  },
  ch2_s3: {
    id: 'ch2_s3', chapter: 2,
    title: 'The Viral Moment',
    story: 'A video of you at a school event is posted online. Someone you don\'t know shares it. Within 24 hours it has 50,000 views. Comments include your school name and what neighbourhood you live in.',
    situation: 'People in the comments are piecing together who you are from the video\'s background details. You didn\'t post it — a classmate did.',
    choices: [
      {
        text: 'Ask your classmate to delete it immediately',
        outcome: 'They delete it, but screenshots exist. This is why digital footprints are permanent — you can remove the original but not every copy.',
        privacyScore: 1, xpReward: 40,
        next: null,
      },
      {
        text: 'Report it to the platform for location/personal info exposure',
        outcome: 'Platforms have policies against doxxing. The video is removed within hours. You\'ve also helped establish a pattern that protects others.',
        privacyScore: 2, xpReward: 70,
        next: null,
      },
      {
        text: 'It\'s already viral — nothing I can do',
        outcome: 'Actually, you have rights. Under Canadian law, the person who posted it may be liable. You can report to the platform AND contact a digital rights organization.',
        privacyScore: -1, xpReward: 20,
        next: null,
      },
    ],
  },

  // ─── Chapter 3: The Permission Game ──────────────────────────────────────
  ch3_s1: {
    id: 'ch3_s1', chapter: 3,
    title: 'Download This Free Game',
    story: 'You find a free mobile game you want to try. During install, it asks for permissions before you can play.',
    situation: 'Permission requests: Microphone, Camera, Contacts (all your friends\' numbers), Location (always, even when not in use), and Storage. The game is a simple puzzle game.',
    choices: [
      {
        text: 'Allow all — I want the full experience',
        outcome: 'A puzzle game now has your microphone, your contacts\' phone numbers, and knows where you are 24/7. None of these are needed to play puzzles. You\'ve been over-permissioned.',
        privacyScore: -2, xpReward: 10,
        next: 'ch3_s2',
      },
      {
        text: 'Deny all and see if the game still works',
        outcome: 'The game works perfectly. No permission was actually required — they were collecting data as a bonus. Always test with minimal permissions first.',
        privacyScore: 2, xpReward: 70,
        next: 'ch3_s2',
      },
      {
        text: 'Only allow storage — that seems fair',
        outcome: 'Reasonable compromise, but check: does a puzzle game really need storage access? It should be able to save progress without accessing your whole file system.',
        privacyScore: 1, xpReward: 40,
        next: 'ch3_s2',
      },
    ],
  },
  ch3_s2: {
    id: 'ch3_s2', chapter: 3,
    title: 'The Smart Device',
    story: 'Your family gets a smart speaker. It\'s always listening for the wake word. Your parents ask if it\'s okay to put it in your room.',
    situation: 'The device manufacturer\'s privacy policy says recordings may be reviewed by employees to "improve accuracy." It also shares data with "trusted third parties."',
    choices: [
      {
        text: 'Sure, it\'s convenient',
        outcome: 'Your conversations in your room are now being processed by a corporate server. Snippets may be reviewed by human employees. It\'s in the terms of service — but most people never read it.',
        privacyScore: -2, xpReward: 10,
        next: 'ch3_s3',
      },
      {
        text: 'Keep it in a common area, not my bedroom',
        outcome: 'Smart choice. Your private space stays private. The speaker works fine in the living room where you control when you\'re near it.',
        privacyScore: 2, xpReward: 60,
        next: 'ch3_s3',
      },
      {
        text: 'Ask to check the privacy settings first',
        outcome: 'You find a setting to delete voice history automatically. You also turn off human review. You didn\'t get full privacy but you got meaningful improvement.',
        privacyScore: 1, xpReward: 50,
        next: 'ch3_s3',
      },
    ],
  },
  ch3_s3: {
    id: 'ch3_s3', chapter: 3,
    title: 'The School App',
    story: 'Your school introduces a new app that tracks attendance using facial recognition. It also monitors which students are "paying attention" in class using camera analysis.',
    situation: 'A company collects biometric data on minors. Under Canadian law, this requires explicit informed consent from parents. Your school says it\'s mandatory.',
    choices: [
      {
        text: 'Use it — school says it\'s required',
        outcome: 'Your biometric data (face map) is now held by a private company. Unlike a password, you can never change your face. This data could follow you for decades.',
        privacyScore: -2, xpReward: 10,
        next: null,
      },
      {
        text: 'Ask your parents to submit a formal objection',
        outcome: 'Under PIPEDA, parents can object to collection of biometric data from minors. Schools have been successfully challenged on this. Your objection has legal weight.',
        privacyScore: 2, xpReward: 90,
        next: null,
      },
      {
        text: 'Research what data they collect and who owns it',
        outcome: 'You find the app is owned by a US company, meaning your data crosses borders into US jurisdiction — where Canadian privacy law no longer protects it. This is a significant finding.',
        privacyScore: 2, xpReward: 80,
        next: null,
      },
    ],
  },
};
